import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

// ─── Styling helpers ──────────────────────────────────────────────────────────
const label = {
  display: 'block',
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '1.4px',
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  marginBottom: '6px',
};
const inputBase = {
  display: 'block',
  width: '100%',
  background: 'var(--bg-panel)',
  border: '1px solid var(--border-medium)',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  fontSize: '12px',
  padding: '10px 14px',
  outline: 'none',
  borderRadius: 0,
  transition: 'border-color 0.15s',
};
const inputError = { ...inputBase, borderColor: 'var(--brand-primary)' };
const errMsg = {
  fontSize: '9px',
  color: 'var(--brand-primary)',
  letterSpacing: '0.8px',
  marginTop: '4px',
};

// Replace with your Formspree form ID once you sign up at formspree.io
// e.g. process.env.REACT_APP_FORMSPREE_ID = "xpwzlkjv"
const FORMSPREE_ENDPOINT = `https://formspree.io/f/${process.env.REACT_APP_FORMSPREE_ID || 'YOUR_FORM_ID'}`;

export default function Contact() {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ mode: 'onBlur' });

  const onSubmit = async (data) => {
    if (data.hp_url) {
      // Silently drop bot submissions
      setStatus('success');
      return;
    }
    setStatus('submitting');
    try {
      // 1 — send email via Formspree
      const fRes = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
      });

      // 2 — store in MongoDB (best-effort; silently ignores if backend is offline)
      fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, source: 'contact-form' }),
      }).catch(err => {
        console.warn('[lead] mirror to backend failed', err);
        if (window.posthog) window.posthog.capture('lead_mirror_failed', { error: String(err) });
      });

      if (fRes.ok) {
        setStatus('success');
        reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  // ── Success state ──────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <section id="contact" className="py-20 px-6 lg:px-[7.69%] bg-bg-primary border-b border-border-medium">
        <div className="max-w-[640px] mx-auto text-center py-20">
          <CheckCircle className="w-10 h-10 text-brand-primary mx-auto mb-6" strokeWidth={1.5} />
          <span className="section-tag">/Message Received</span>
          <h3 className="display-large mt-2 mb-4">We'll be in touch.</h3>
          <p className="body-medium max-w-md mx-auto">
            Your information reached <strong style={{ color: 'var(--text-primary)' }}>founder@falcon42.ai</strong>.
            Expect a response within one business day.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="btn-secondary mt-8"
          >
            Submit Another
          </button>
        </div>
      </section>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <section id="contact" className="py-20 px-6 lg:px-[7.69%] bg-bg-primary border-b border-border-medium relative">
      <div className="max-w-[1400px] mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="section-tag">/Get Started</span>
          <h3 className="display-large mb-3">Let's Build Something Together</h3>
          <p className="body-medium max-w-2xl mx-auto">
            Tell us what you're working on. We'll qualify the fit and come back with a concrete automation plan — not a sales pitch.
          </p>
        </div>

        {/* Two-column layout: form left, info right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Form panel */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="lg:col-span-2 terminal-card p-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              {/* Full Name */}
              <div>
                <label style={label}>
                  Full Name <span style={{ color: 'var(--brand-primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Jane Smith"
                  style={errors.name ? inputError : inputBase}
                  {...register('name', { required: 'Required' })}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--brand-primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.name ? 'var(--brand-primary)' : 'var(--border-medium)')}
                />
                {errors.name && <p style={errMsg}>{errors.name.message}</p>}
              </div>

              {/* Work Email */}
              <div>
                <label style={label}>
                  Work Email <span style={{ color: 'var(--brand-primary)' }}>*</span>
                </label>
                <input
                  type="email"
                  placeholder="jane@company.com"
                  style={errors.email ? inputError : inputBase}
                  {...register('email', {
                    required: 'Required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' },
                  })}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--brand-primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.email ? 'var(--brand-primary)' : 'var(--border-medium)')}
                />
                {errors.email && <p style={errMsg}>{errors.email.message}</p>}
              </div>

              {/* Company */}
              <div>
                <label style={label}>
                  Company <span style={{ color: 'var(--brand-primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Acme Corp"
                  style={errors.company ? inputError : inputBase}
                  {...register('company', { required: 'Required' })}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--brand-primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.company ? 'var(--brand-primary)' : 'var(--border-medium)')}
                />
                {errors.company && <p style={errMsg}>{errors.company.message}</p>}
              </div>

              {/* Role */}
              <div>
                <label style={label}>
                  Your Role <span style={{ color: 'var(--brand-primary)' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="CEO, CTO, Head of Operations…"
                  style={errors.role ? inputError : inputBase}
                  {...register('role', { required: 'Required' })}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--brand-primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.role ? 'var(--brand-primary)' : 'var(--border-medium)')}
                />
                {errors.role && <p style={errMsg}>{errors.role.message}</p>}
              </div>

              {/* Company Size */}
              <div>
                <label style={label}>Company Size</label>
                <select
                  style={{ ...inputBase, cursor: 'pointer', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23AAA080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '32px' }}
                  {...register('company_size')}
                >
                  <option value="">Select…</option>
                  <option value="1-10">1 – 10 employees</option>
                  <option value="11-50">11 – 50 employees</option>
                  <option value="51-200">51 – 200 employees</option>
                  <option value="200+">200+ employees</option>
                </select>
              </div>

              {/* Timeline */}
              <div>
                <label style={label}>Timeline</label>
                <select
                  style={{ ...inputBase, cursor: 'pointer', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23AAA080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '32px' }}
                  {...register('timeline')}
                >
                  <option value="">Select…</option>
                  <option value="immediate">Ready to start immediately</option>
                  <option value="1-3mo">Within 1 – 3 months</option>
                  <option value="3-6mo">3 – 6 months out</option>
                  <option value="exploring">Still exploring</option>
                </select>
              </div>
            </div>

            {/* What to automate — full width */}
            <div className="mb-6">
              <label style={label}>
                What do you want to automate? <span style={{ color: 'var(--brand-primary)' }}>*</span>
              </label>
              <textarea
                rows={4}
                placeholder="Describe the bottleneck, repetitive process, or workflow you'd like to eliminate or accelerate. The more specific, the better."
                style={{ ...( errors.challenge ? inputError : inputBase ), resize: 'vertical', minHeight: '100px' }}
                {...register('challenge', { required: 'Required', minLength: { value: 20, message: 'Give us at least 20 characters' } })}
                onFocus={(e) => (e.target.style.borderColor = 'var(--brand-primary)')}
                onBlur={(e) => (e.target.style.borderColor = errors.challenge ? 'var(--brand-primary)' : 'var(--border-medium)')}
              />
              {errors.challenge && <p style={errMsg}>{errors.challenge.message}</p>}
            </div>

            {/* Honeypot — hidden from real users, traps bots */}
            <input
              type="text"
              {...register('hp_url')}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                top: '-9999px',
                width: 0,
                height: 0,
                opacity: 0,
              }}
            />

            {/* Submit row */}
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-primary"
                style={{ opacity: status === 'submitting' ? 0.65 : 1 }}
              >
                {status === 'submitting' ? (
                  'Sending…'
                ) : (
                  <>
                    Submit — We'll be in touch
                    <Send className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
              <span style={{ fontSize: '9px', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>
                Goes to founder@falcon42.ai &nbsp;·&nbsp; No spam, ever.
              </span>
            </div>

            {status === 'error' && (
              <div className="mt-4 flex items-center gap-2 text-brand-primary" style={{ fontSize: '11px' }}>
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                Something went wrong — try emailing us directly at{' '}
                <a href="mailto:founder@falcon42.ai" style={{ color: 'var(--brand-primary)', textDecoration: 'underline' }}>
                  founder@falcon42.ai
                </a>
              </div>
            )}
          </form>

          {/* Right panel — contact details + what happens next */}
          <div className="flex flex-col gap-6">
            <div className="terminal-card p-6">
              <span className="section-tag">/Direct Contact</span>
              <a
                href="mailto:founder@falcon42.ai"
                className="block text-brand-primary hover:underline mt-1"
                style={{ fontSize: '13px', fontWeight: 600, wordBreak: 'break-all' }}
              >
                founder@falcon42.ai
              </a>
              <p className="body-medium mt-3">
                Prefer a direct email? Skip the form and reach our founder directly.
              </p>
            </div>

            <div className="terminal-card p-6">
              <span className="section-tag">/What Happens Next</span>
              <ul className="mt-3 flex flex-col gap-3" style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {[
                  'We review your submission within 1 business day.',
                  'If there\'s a fit, you\'ll get a 30-min strategy call invite.',
                  'No retainer until you\'ve seen a concrete automation plan.',
                ].map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-brand-primary font-bold shrink-0">→</span>
                    {step}
                  </li>
                ))}
              </ul>
            </div>

            <div className="terminal-card p-6">
              <span className="section-tag">/Response Time</span>
              <p className="body-medium mt-2">
                Typical reply within <strong style={{ color: 'var(--text-primary)' }}>24 hours</strong> on business days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
